import { AxiosResponse } from 'axios';
import { appContextDelete, appContextGet, appContextPost, appContextPut } from './httpCommon/appAxios.service';
import { ACCOUNT_STORAGE_ITEM_ENDPOINTS } from '../constants/apiEndpoints';
import { StorageTankOptions } from '../types/enums/storageTank.enum';
import { PagedResponse, StandardParams } from '../types/interfaces';
import {
  AccountStorageCanister,
  AccountStorageCanisterPost,
  AccountStorageCanisterPut,
  AccountStorageSite,
  AccountStorageSitePost,
  AccountStorageSitePut,
  AccountStorageTank,
  AccountStorageTankPost,
  AccountStorageTankPut,
} from '../types/interfaces/accountStorage.interfaces';

export const createAccountStorageSite = (payload: AccountStorageSitePost): Promise<AxiosResponse<number, any>> => {
  return appContextPost(ACCOUNT_STORAGE_ITEM_ENDPOINTS.SITES, payload);
};

export const createAccountStorageTank = (payload: AccountStorageTankPost): Promise<AxiosResponse<number, any>> => {
  return appContextPost(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS, payload);
};

export const createAccountStorageCanister = (payload: AccountStorageCanisterPost): Promise<AxiosResponse<number, any>> => {
  return appContextPost(ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS, payload);
};

export const deleteAccountStorageTank = (tankId: string | number): Promise<AxiosResponse<number, any>> => {
  return appContextDelete(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS_ + tankId.toString());
};

export const deleteAccountStorageSite = (siteId: string | number): Promise<AxiosResponse<number, any>> => {
  return appContextDelete(ACCOUNT_STORAGE_ITEM_ENDPOINTS.SITES_ + siteId.toString());
};

export const deleteAccountStorageCanister = (canisterId: string | number): Promise<AxiosResponse<number, any>> => {
  return appContextDelete(ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS_ + canisterId.toString());
};

export const getAccountStorageSites = (
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageSite[] | PagedResponse<AccountStorageSite>, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.SITES, params);
};

export const getAccountStorageTanks = (
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageTank[] | PagedResponse<AccountStorageTank>, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS, params);
};

export const getAccountStorageCanisters = (
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageCanister[] | PagedResponse<AccountStorageCanister>, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS, params);
};

export const updateAccountStorageSite = (
  siteId: string | number,
  payload: AccountStorageSitePut,
): Promise<AxiosResponse<AccountStorageSite, any>> => {
  return appContextPut(ACCOUNT_STORAGE_ITEM_ENDPOINTS.SITES_ + siteId.toString(), payload);
};

export const updateAccountStorageTank = (
  tankId: string | number,
  payload: AccountStorageTankPut,
): Promise<AxiosResponse<AccountStorageTank, any>> => {
  return appContextPut(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS_ + tankId.toString(), payload);
};

export const updateAccountStorageCanister = (
  canisterId: string | number,
  payload: AccountStorageCanisterPut,
): Promise<AxiosResponse<AccountStorageCanister, any>> => {
  return appContextPut(ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS_ + canisterId.toString(), payload);
};

export const getAccountStorageSiteById = (
  siteId: string | number,
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageSite, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.SITES_ + siteId, params);
};

export const getAccountStorageTankById = (
  tankId: string | number,
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageTank, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS_ + tankId, params);
};

export const getAccountStorageCanisterById = (
  canisterId: string | number,
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageCanister, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS_ + canisterId, params);
};

export const getAccountStorageTanksBySite = (
  siteId: string,
  storageTankOptions: StorageTankOptions,
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageTank[] | PagedResponse<AccountStorageTank>, any>> => {
  let isTravelingTank: string = ';isTravelingTank eq ';
  switch (storageTankOptions) {
    case StorageTankOptions.Stationary:
      isTravelingTank += 'false';
      break;
    case StorageTankOptions.Travel:
      isTravelingTank += 'true';
      break;
    default:
      isTravelingTank = '';
      break;
  }
  const filter = ['active eq true;accountStorageSiteId eq ' + siteId + isTravelingTank, params?.filter].join(';');
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS, { ...params, filter: filter });
};

export const getAccountStorageCanisterByTank = (
  tankId: string,
  params?: StandardParams,
): Promise<AxiosResponse<AccountStorageCanister[] | PagedResponse<AccountStorageCanister>, any>> => {
  const filter = ['accountStorageTankId eq ' + tankId, params?.filter].join(';');
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS, { ...params, filter: filter });
};

export const getAccountStorageCanisterCount = (
  accountStorageCanisterId: number | string,
): Promise<AxiosResponse<number, any>> => {
  return appContextGet(
    ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTERS_ +
      accountStorageCanisterId.toString() +
      ACCOUNT_STORAGE_ITEM_ENDPOINTS.CANISTER_COUNT,
  );
};

export const getAccountStorageTankInventoryCount = (tankId: string | number): Promise<AxiosResponse<number, any>> => {
  return appContextGet(ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANKS_ + tankId.toString() + ACCOUNT_STORAGE_ITEM_ENDPOINTS.TANK_COUNT);
};
